import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Eye, EyeOff, Image as ImageIcon, X, Check, Search, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCollections = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // category object or null

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories/all/flat`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      if (error.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const organizeCategories = () => {
    const organized = [];
    const parents = categories.filter(c => !c.parent_id);
    parents.forEach(parent => {
      organized.push({ ...parent, displayName: parent.name, level: 0 });
      const children = categories.filter(c => c.parent_id === parent.id);
      children.forEach(child => {
        organized.push({ ...child, displayName: `  └─ ${child.name}`, level: 1 });
      });
    });
    return organized;
  };

  const updateCategory = async (category, patch) => {
    setUpdating(category.id);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_URL}/api/admin/categories/${category.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...category, ...patch })
        }
      );
      if (!response.ok) throw new Error('Update failed');
      setCategories(categories.map(c => c.id === category.id ? { ...c, ...patch } : c));
      return true;
    } catch (e) {
      console.error(e);
      alert('Kategori güncellenirken hata oluştu!');
      return false;
    } finally {
      setUpdating(null);
    }
  };

  const toggleCollectionVisibility = async (category) => {
    await updateCategory(category, { show_in_collections: !category.show_in_collections });
  };

  const handleProductPicked = async (product) => {
    if (!pickerFor) return;
    await updateCategory(pickerFor, {
      collection_image: product.image,
      collection_product_id: product.id
    });
    setPickerFor(null);
  };

  const handleClearImage = async (category) => {
    if (!window.confirm('Koleksiyon görselini temizlemek istediğinizden emin misiniz? Varsayılan görsel kullanılacak.')) return;
    await updateCategory(category, { collection_image: null, collection_product_id: null });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const organizedCategories = organizeCategories();

  return (
    <AdminLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Koleksiyonlar Yönetimi</h1>
          <p className="text-gray-600">
            Ana sayfada "Koleksiyonlar" bölümünde hangi kategorilerin görüneceğini belirleyin ve her biri için bir kapak görseli seçin.
          </p>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Not:</strong> "Görsel Seç" butonuyla o kategorinin altındaki ürünlerden birinin görselini kapak olarak seçebilirsiniz. Seçmezseniz kategorinin ilk ürününün görseli otomatik kullanılır.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Kategori Adı</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Slug</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Kapak</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Görsel Seç</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Durum</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Göster</th>
                </tr>
              </thead>
              <tbody>
                {organizedCategories.map((category) => (
                  <tr key={category.id} className={`border-b hover:bg-gray-50 ${category.level === 1 ? 'bg-gray-50/50' : ''}`}>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${category.level === 1 ? 'text-gray-600 font-mono text-sm' : 'text-gray-800'}`}>
                          {category.displayName}
                        </span>
                        {category.level === 0 && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Ana</span>
                        )}
                        {category.level === 1 && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-semibold">Alt</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-mono">
                        {category.slug}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      {category.collection_image ? (
                        <div className="relative inline-block group">
                          <img 
                            src={category.collection_image} 
                            alt=""
                            className="w-14 h-14 rounded-lg object-cover border-2 border-green-400 mx-auto"
                            data-testid={`col-image-${category.slug}`}
                          />
                          <button
                            onClick={() => handleClearImage(category)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                            aria-label="Görseli kaldır"
                            data-testid={`clear-col-image-${category.slug}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Otomatik</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => setPickerFor(category)}
                        data-testid={`pick-image-${category.slug}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-xs font-semibold"
                      >
                        <ImageIcon size={14} /> {category.collection_image ? 'Değiştir' : 'Görsel Seç'}
                      </button>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        category.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {category.enabled ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => toggleCollectionVisibility(category)}
                        disabled={updating === category.id}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg font-semibold transition text-xs ${
                          category.show_in_collections
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        } ${updating === category.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {updating === category.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : category.show_in_collections ? (
                          <><Eye size={14} /> Gösterilir</>
                        ) : (
                          <><EyeOff size={14} /> Gizli</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
          <div>
            Toplam {categories.length} kategori ({categories.filter(c => !c.parent_id).length} ana, {categories.filter(c => c.parent_id).length} alt)
          </div>
          <div>
            Koleksiyonlarda görünecek: <span className="font-bold text-green-600">{categories.filter(c => c.show_in_collections).length}</span>
            {' | '}Özel kapak görseli: <span className="font-bold text-purple-600">{categories.filter(c => c.collection_image).length}</span>
          </div>
        </div>
      </div>

      {/* Product picker modal */}
      {pickerFor && (
        <ProductPickerModal
          category={pickerFor}
          onPick={handleProductPicked}
          onClose={() => setPickerFor(null)}
        />
      )}
    </AdminLayout>
  );
};

// ================= Product Picker Modal =================
const ProductPickerModal = ({ category, onPick, onClose }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/products?category=${encodeURIComponent(category.slug)}`);
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data.products || []);
        setProducts(list);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category.slug]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || String(p.id).includes(search)
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="product-picker-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div>
            <h2 className="text-lg font-bold">Koleksiyon Kapak Görseli Seçin</h2>
            <p className="text-xs opacity-90 mt-1">
              <strong>{category.name}</strong> kategorisindeki ürünlerden birini seçin
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ürün adı veya ID ile ara..."
              data-testid="product-picker-search"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 size={24} className="animate-spin mr-2" /> Yükleniyor...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              Bu kategoride ürün bulunamadı
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map(p => {
                const isSelected = category.collection_product_id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => onPick(p)}
                    data-testid={`product-pick-${p.id}`}
                    className={`group relative bg-white border-2 rounded-lg overflow-hidden hover:shadow-lg transition ${
                      isSelected ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-200 hover:border-purple-400'
                    }`}
                  >
                    {isSelected && (
                      <span className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1 z-10">
                        <Check size={14} />
                      </span>
                    )}
                    <div className="aspect-square bg-gray-100">
                      <img 
                        src={p.image} 
                        alt={p.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />
                    </div>
                    <div className="p-2 text-left">
                      <span className="text-[10px] bg-gray-200 px-1.5 py-0.5 rounded font-mono">#{p.id}</span>
                      <p className="text-xs text-gray-700 line-clamp-2 mt-1">{p.name}</p>
                      <p className="text-xs font-semibold text-green-700 mt-1">{p.price?.toLocaleString('tr-TR')} TL</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 px-6 py-3 flex items-center justify-between">
          <span className="text-xs text-gray-600">
            {!loading && `${filtered.length} ürün listeleniyor`}
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium text-sm"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCollections;
